import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Data hooks for the competition master-schedule import (Phase 3). The parse +
// home-studio match runs client-side via @pirouette/shared; these hooks just
// read/write the studio identity and commit the matched routines through the
// admin-gated import_competition_schedule RPC.

interface RpcError {
  message: string;
}

export interface StudioProfile {
  name: string;
  aliases: string[];
}

type GetProfileRpc = (fn: 'get_studio_profile') => Promise<{
  data: { name: string; aliases: string[] | null } | null;
  error: RpcError | null;
}>;

export function useStudioProfile() {
  return useQuery({
    queryKey: ['studio-profile'],
    queryFn: async (): Promise<StudioProfile> => {
      const { data, error } = await (supabase.rpc as unknown as GetProfileRpc)(
        'get_studio_profile',
      );
      if (error) throw new Error(error.message);
      return { name: data?.name ?? '', aliases: data?.aliases ?? [] };
    },
  });
}

type SetProfileRpc = (
  fn: 'set_studio_profile',
  args: { p_name: string; p_aliases: string[] },
) => Promise<{ data: { name: string; aliases: string[] | null } | null; error: RpcError | null }>;

export function useSetStudioProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StudioProfile): Promise<StudioProfile> => {
      const { data, error } = await (supabase.rpc as unknown as SetProfileRpc)(
        'set_studio_profile',
        {
          p_name: input.name,
          p_aliases: input.aliases,
        },
      );
      if (error) throw new Error(error.message);
      return { name: data?.name ?? input.name, aliases: data?.aliases ?? input.aliases };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['studio-profile'] });
    },
  });
}

export interface ImportCompetitionRoutineInput {
  title: string;
  category?: string | null;
  size?: string | null;
  num_dancers?: number | null;
  age_division?: string | null;
  level?: string | null;
  entry_number?: string | null;
  scheduled_day?: string | null;
  scheduled_time?: string | null;
  duration_seconds?: number | null;
}

export interface ImportCompetitionInput {
  competition: {
    name: string;
    organizer?: string | null;
    venue_name?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    season?: string | null;
    notes?: string | null;
  };
  routines: ImportCompetitionRoutineInput[];
}

export interface ImportCompetitionResult {
  competition_id: string;
  production_id: string;
  routines_created: number;
}

type ImportRpc = (
  fn: 'import_competition_schedule',
  args: {
    p_competition: ImportCompetitionInput['competition'];
    p_routines: ImportCompetitionRoutineInput[];
  },
) => Promise<{ data: ImportCompetitionResult | null; error: RpcError | null }>;

export function useImportCompetitionSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportCompetitionInput): Promise<ImportCompetitionResult> => {
      const { data, error } = await (supabase.rpc as unknown as ImportRpc)(
        'import_competition_schedule',
        { p_competition: input.competition, p_routines: input.routines },
      );
      if (error) throw new Error(error.message);
      if (!data) throw new Error('Import returned no result');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['competitions'] });
    },
  });
}

// ── Phase 4: PDF OCR via the competition-schedule-parse Edge Function ────────
// Sends the raw PDF to Textract (server-side); the staged PDF is deleted
// immediately after OCR. Returns delimited text that flows into the SAME
// client-side scan as paste/CSV. No PDF or dancer PII is persisted here.
export interface ParsePdfResult {
  text: string;
  pages: number;
  table_count: number;
  chars: number;
  truncated: boolean;
}

// Supabase FunctionsHttpError carries the Response in `context`; read the
// function's specific error code (mfa_required, not_configured, …) from it so
// the UI can show a useful message. Typed as `unknown` to satisfy strict lint.
async function parseFunctionErrorCode(error: unknown): Promise<string> {
  if (error !== null && typeof error === 'object' && 'context' in error) {
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const body = (await ctx.clone().json()) as { error?: unknown; detail?: unknown };
        const code = body.error ?? body.detail;
        if (typeof code === 'string') return code;
      } catch {
        // non-JSON error body — fall through to the generic message
      }
    }
  }
  if (error instanceof Error) return error.message;
  return 'ocr_failed';
}

export function useParseCompetitionPdf() {
  return useMutation({
    mutationFn: async (file: File): Promise<ParsePdfResult> => {
      const result = (await supabase.functions.invoke<ParsePdfResult>(
        'competition-schedule-parse',
        {
          body: file,
        },
      )) as { data: ParsePdfResult | null; error: unknown };
      if (result.error) throw new Error(await parseFunctionErrorCode(result.error));
      if (!result.data) throw new Error('OCR failed');
      return result.data;
    },
  });
}
